import { suggestNextDueOn } from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import {
  FLEET_INSPECTION_KIND_LABELS,
  FLEET_INSPECTION_RESULT_LABELS,
  formatDeDate,
  formatFleetDueDate,
  isFleetDateOverdue,
} from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import {
  useFleetInspections,
  useFleetServiceDue,
  useFleetVehicle,
  useRecordFleetInspection,
} from '@/products/fleet/features/use-fleet';

export function FleetVehicleDeadlinesTab({
  vehicleId,
  manage,
}: {
  vehicleId: string;
  manage: boolean;
}) {
  const vehicle = useFleetVehicle(vehicleId);
  const inspections = useFleetInspections(vehicleId);
  const serviceDue = useFleetServiceDue(vehicleId);
  const record = useRecordFleetInspection(vehicleId);
  const current = vehicle.data;
  const [kind, setKind] = useState<'hu' | 'uvv_vehicle'>('hu');
  const [performedOn, setPerformedOn] = useState('');
  const [result, setResult] = useState<'passed' | 'failed'>('passed');
  const [nextDueOn, setNextDueOn] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const suggestion =
    current && performedOn
      ? suggestNextDueOn({
          kind,
          category: current.category,
          performedOn,
          huNextMonths: current.huNextMonths,
          uvvIntervalMonths: current.uvvIntervalMonths,
        })
      : '';

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await record.mutateAsync({
        kind,
        performedOn,
        result,
        nextDueOn: nextDueOn || suggestion || undefined,
        note: note.trim() || undefined,
      });
      setNote('');
      toast.success('Prüfung gespeichert.');
    } catch (err: unknown) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : 'Die Prüfung konnte nicht gespeichert werden.';
      setError(message);
    }
  }

  return (
    <div className="space-y-6">
      <dl className="grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-muted-foreground">Nächste HU</dt>
          <dd
            className={
              current?.hu.missingRegistration
                ? 'font-medium text-amber-800 dark:text-amber-300'
                : isFleetDateOverdue(current?.hu.dueOn)
                  ? 'font-medium text-destructive'
                  : undefined
            }
          >
            {current?.hu.missingRegistration
              ? 'Erstzulassung fehlt'
              : current?.hu.dueOn
                ? formatFleetDueDate(current.hu.dueOn)
                : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Nächste UVV-Prüfung</dt>
          <dd
            className={
              current?.uvv.missingRegistration
                ? 'font-medium text-amber-800 dark:text-amber-300'
                : isFleetDateOverdue(current?.uvv.dueOn)
                  ? 'font-medium text-destructive'
                  : undefined
            }
          >
            {current?.uvv.missingRegistration
              ? 'Erstzulassung fehlt'
              : current?.uvv.dueOn
                ? formatFleetDueDate(current.uvv.dueOn)
                : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Service (Kilometer)</dt>
          <dd>
            {serviceDue.data?.byKm.status === 'no_interval'
              ? '—'
              : (serviceDue.data?.byKm.message ?? 'in Ordnung')}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Service (Datum)</dt>
          <dd>
            {serviceDue.data?.byDate.status === 'no_interval'
              ? '—'
              : (serviceDue.data?.byDate.message ?? 'in Ordnung')}
          </dd>
        </div>
      </dl>
      {inspections.data && inspections.data.items.length > 0 ? (
        <ul className={`${fleetTableFrameClassName} divide-y text-sm`}>
          {inspections.data.items.map((row) => (
            <li key={row.id} className="p-3">
              {FLEET_INSPECTION_KIND_LABELS[row.kind]} · {formatDeDate(row.performedOn)} ·{' '}
              {FLEET_INSPECTION_RESULT_LABELS[row.result]} · fällig {formatDeDate(row.nextDueOn)}
            </li>
          ))}
        </ul>
      ) : null}
      {manage ? (
        <form className={`${fleetPanelClassName} space-y-3`} onSubmit={onSubmit}>
          <h3 className="text-base font-semibold">Prüfung erfassen</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="insp-kind">Art</Label>
              <Select
                value={kind}
                onValueChange={(value) => setKind(value as 'hu' | 'uvv_vehicle')}
              >
                <SelectTrigger id="insp-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hu">HU</SelectItem>
                  <SelectItem value="uvv_vehicle">UVV-Prüfung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="insp-date">Datum</Label>
              <Input
                id="insp-date"
                type="date"
                value={performedOn}
                onChange={(event) => setPerformedOn(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="insp-result">Ergebnis</Label>
              <Select
                value={result}
                onValueChange={(value) => setResult(value as 'passed' | 'failed')}
              >
                <SelectTrigger id="insp-result" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="passed">bestanden</SelectItem>
                  <SelectItem value="failed">nicht bestanden</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="insp-next">Nächste Fälligkeit</Label>
              <Input
                id="insp-next"
                type="date"
                value={nextDueOn || suggestion}
                onChange={(event) => setNextDueOn(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="insp-note">Notiz</Label>
            <Input id="insp-note" value={note} onChange={(event) => setNote(event.target.value)} />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button type="submit" disabled={record.isPending}>
            Prüfung speichern
          </Button>
        </form>
      ) : null}
    </div>
  );
}
